import { useState, useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { AuthContext } from '../context/AuthContext';
import { apiClient } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useUnreadTitle } from './useUnreadTitle';

/**
 * Custom React hook to track global unread messages count in real-time across the app.
 * Listens to unreadCount fields in Firebase Firestore chat room documents.
 */
export function useUnreadCount() {
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, firebaseUser } = useContext(AuthContext);
  const currentUserUid = user?.id || firebaseUser?.uid;
  const isOnline = useSelector(state => state.ui.networkStatus.isOnline);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Delegate tab title (with route-change resilience) to useUnreadTitle
  useUnreadTitle(unreadCount);

  useEffect(() => {
    // Wait until both the PWA user and the Firebase session are fully ready
    if (!currentUserUid || !firebaseUser || !isOnline) {
      setUnreadCount(0);
      return;
    }

    let unsubscribes = [];
    const roomStates = {};
    let activeRoomIds = new Set();
    let pollInterval = null;

    const setupListeners = async () => {
      try {
        console.log('[useUnreadCount] Fetching rooms from API for unread badge count...');
        const response = await apiClient.get(API_ENDPOINTS.chats.rooms);
        const roomsList = response.data?.data?.data || response.data?.data || response.data || [];

        if (!isMountedRef.current) return;

        if (roomsList.length === 0) {
          setUnreadCount(0);
          return;
        }

        // If at least one room exists, stop global polling
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }

        const fetchedRoomIds = new Set(
          roomsList.filter(r => r.room_id).map(r => r.room_id)
        );

        let hasNewRooms = false;
        for (let rId of fetchedRoomIds) {
          if (!activeRoomIds.has(rId)) {
            hasNewRooms = true;
            break;
          }
        }

        if (hasNewRooms || fetchedRoomIds.size !== activeRoomIds.size) {
          console.log(`[useUnreadCount] Rooms changed. Updating listeners...`);
          unsubscribes.forEach(unsub => unsub());
          unsubscribes = [];
          activeRoomIds = fetchedRoomIds;

          unsubscribes = Array.from(activeRoomIds).map(rid => {
            const roomRef = doc(db, 'chats', rid);

            return onSnapshot(roomRef, (docSnap) => {
              if (!isMountedRef.current) return;

              if (docSnap.exists()) {
                const data = docSnap.data();
                const unread = data.unreadCounts?.[String(currentUserUid)] || 0;
                roomStates[rid] = unread;
              } else {
                roomStates[rid] = 0;
              }

              // Calculate total unread count
              const totalUnread = Object.values(roomStates).reduce((sum, val) => sum + val, 0);
              setUnreadCount(totalUnread);
            }, (err) => {
              console.warn(`[useUnreadCount] Failed to listen to room ${rid}:`, err.message);
            });
          });
        }
      } catch (error) {
        console.warn('🔴 [useUnreadCount] Failed to fetch rooms or setup unread listeners:', error.message);
      }
    };

    setupListeners();
    pollInterval = setInterval(setupListeners, 20000);

    return () => {
      console.log('[useUnreadCount] Cleaning up unread count listeners');
      unsubscribes.forEach(unsub => unsub());
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [currentUserUid, firebaseUser, isOnline]);

  return unreadCount;
}
